import React from 'react'

import { type IconName, iconMapping } from '~components/icons/iconMapping'
import { typography } from '~tokens/internal'

import { type SemanticIconName, resolveSemanticIconName } from './icons/semanticIcons'

export type { SemanticIconName } from './icons/semanticIcons'

export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  name: SemanticIconName | IconName
  size?: string | number
  fontSize?: 'inherit' | 'small' | 'medium' | 'large'
  color?: string
  fill?: 1 | 0
  /**
   * Material Symbols font family variant. Defaults to `'rounded'` to match the
   * rest of the internal design system. Use `'outlined'` when a specific icon
   * is only available (or visually preferable) in the outlined family.
   */
  variant?: 'rounded' | 'outlined'
}

const fontSizeMap = {
  inherit: undefined,
  small: typography.fontSize.lg,
  medium: typography.fontSize.xl,
  large: typography.fontSize.xxl,
}

const getBaseIconStyles = (fill: 1 | 0): React.CSSProperties => ({
  fontVariationSettings: `'FILL' ${fill}, 'wght' ${typography.fontWeight.regular}, 'GRAD' 0, 'opsz' 24`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
})

const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  (
    {
      name,
      size,
      fontSize,
      color,
      fill = 1,
      variant = 'rounded',
      className,
      style,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const resolvedSemantic = resolveSemanticIconName(name)
    const symbolName = (resolvedSemantic || iconMapping[name as IconName] || name) as string

    const normalizedSize = typeof size === 'number' ? `${size}px` : size
    const computedFontSize = normalizedSize
      ? normalizedSize
      : fontSize && fontSize in fontSizeMap
        ? fontSizeMap[fontSize as keyof typeof fontSizeMap]
        : typography.fontSize.xl

    return (
      <span
        ref={ref}
        className={`material-symbols-${variant} ${className || ''}`}
        aria-hidden={ariaLabel ? undefined : 'true'}
        aria-label={ariaLabel}
        style={{
          ...getBaseIconStyles(fill),
          fontSize: computedFontSize,
          color: color || 'inherit',
          width: normalizedSize || (fontSize === 'inherit' ? undefined : computedFontSize),
          height: normalizedSize || (fontSize === 'inherit' ? undefined : computedFontSize),
          ...style,
        }}
        {...props}
      >
        {symbolName}
      </span>
    )
  },
)

Icon.displayName = 'Icon'

export default Icon
